import {
  Component, ViewEncapsulation, ChangeDetectionStrategy, Input, Output, EventEmitter, OnInit
} from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import isEmpty from 'lodash-es/isEmpty';
import { Observable } from 'rxjs';

import { environment as ENV } from '@environment';
import { OptionSelected } from '@booking/entities/booking-arrangement';
import { TRAVELER_FORM_INPUTS, PERSON_FORM_INPUTS, PERSON_ADDRESS_FORM_INPUTS } from '@booking/constants/forms.constants';
import { CountriesVm } from '@booking/entities/countries';
import { BillingFacade } from '@billing/billing.facade';
import { FindTypes } from '@billing/entities/find-types';
import { BillingCity } from '@billing/entities/billing.payload';
import { FormInputsService } from '@reservation/services/form-inputs.service';
import { Icon } from '@design-system/components/atoms/icon/entities/icon.types';
import { TypographyColor, TypographyFontSize } from '@design-system/components/atoms/typography/entities/typography.types';

@Component({
  selector: 'app-country-selector',
  templateUrl: './country-selector.component.html',
  styleUrls: ['./country-selector.component.sass'],
  encapsulation: ViewEncapsulation.ShadowDom,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class CountrySelectorComponent implements OnInit {

  @Input() form: UntypedFormGroup;
  @Input() defaultCode: string;
  @Input() isSubmitted: boolean;
  @Input() countries: CountriesVm[] = [];
  @Input() canSetCities: boolean = false;
  @Input() isBilling: boolean = false;
  @Input() selector: string = TRAVELER_FORM_INPUTS.NATIONALITY;
  @Input() activeForm: boolean;
  @Input() disabledCountryOptions: boolean = false;
  @Input() isMobile: boolean;

  @Output() selected: EventEmitter<string> = new EventEmitter();

  public currentCountryValue: string;

  public readonly iconLocation = Icon.Location;

  public readonly fontSize = TypographyFontSize;
  public readonly fontColor = TypographyColor;

  constructor(
    private readonly formInputsService: FormInputsService,
    private readonly facade: BillingFacade,
  ) { }


  ngOnInit(): void {
    if (this.defaultCountryIndex > -1) this.onCountrySelected({ value: this.defaultCountry, index: this.defaultCountryIndex });
  }

  get cities$(): Observable<BillingCity[]> {
    return this.facade.cities$;
  }

  get defaultCountryIndex(): number {
    const countryIndexByCode = this.countries.findIndex(c => c.code === this.defaultCode);
    return countryIndexByCode > -1 ? countryIndexByCode : this.countries.findIndex(c => c.code === ENV.countryInfo.iso_code);
  }

  get defaultCountry(): string {
    return this.defaultCountryIndex > -1 ? this.countries[this.defaultCountryIndex].name : '';
  }

  get addressForm(): UntypedFormGroup {
    return this.form?.get(PERSON_FORM_INPUTS.ADDRESS) as UntypedFormGroup;
  }

  get label(): string {
    return this.isBilling ? 'BOOKING_MODULE.BILLING.COUNTRY' : 'BOOKING_MODULE.NATIONALITY';
  }

  get countryNames(): string[] {
    return this.formInputsService.countryNames(this.countries);
  }

  public onCountrySelected(country: OptionSelected): void {
    const { value } = country;
    const fullCountry = this.countries.find(countryData => countryData.name === value.toString())?.code || value.toString();
    if (this.canSetCities) {
      this.setCities(fullCountry);
      this.addressForm.patchValue({ [PERSON_ADDRESS_FORM_INPUTS.COUNTRY_CODE]: fullCountry });
      this.form?.patchValue({ [PERSON_FORM_INPUTS.ADDRESS]: this.addressForm.value });
    } else {
      this.form?.patchValue({ [TRAVELER_FORM_INPUTS.NATIONALITY]: fullCountry });
    }
    this.selected.emit(fullCountry);
    this.currentCountryValue = value.toString();
  }

  public isFieldInvalid(field: string): boolean {
    return this.formInputsService.isFieldInvalid(field, this.form, this.isSubmitted);
  }

  public setCities(code: string): void {
    if (this.activeForm && this.canSetCities) {
      this.cities$.subscribe((cities: BillingCity[]) => {
        if ((isEmpty(cities) ||
          (!isEmpty(cities) && cities[0].countrycode.toUpperCase() !== code.toUpperCase()))) {
          this.facade.fetchBillingCities({ code, type: FindTypes.CITY });
        }
      }).unsubscribe();
    }
  }

}
